
import { supabase } from "@/integrations/supabase/client";
import { QueryResult } from "../types";

/**
 * Queries related to contract distributions
 */
export const distributionQueries = {
  // Get contracts distribution by contract type
  getContractsDistribution: async (year?: number): Promise<QueryResult> => {
    try {
      const currentYear = year || new Date().getFullYear();
      const startOfYear = new Date(currentYear, 0, 1).toISOString();
      const endOfYear = new Date(currentYear, 11, 31, 23, 59, 59).toISOString();
      
      const { data, error } = await supabase
        .from('contracts')
        .select('contract_type_id, contract_types(name)')
        .gte('created_at', startOfYear)
        .lte('created_at', endOfYear);
      
      if (error) throw error;
      
      // Group by contract type and count
      const typeDistribution: Record<string, number> = {};
      data.forEach(contract => {
        const typeName = contract.contract_types?.name || 'Uncategorized';
        typeDistribution[typeName] = (typeDistribution[typeName] || 0) + 1;
      });
      
      // Convert to array format for charts
      const distribution = Object.entries(typeDistribution).map(([name, value]) => ({
        name,
        value
      }));
      
      return {
        data: { distribution },
        error: null
      };
    } catch (error) {
      console.error("Error fetching contracts distribution:", error);
      return {
        data: { distribution: [] },
        error
      };
    }
  },
  
  // Get contract costs by type
  getContractCostsByType: async (year?: number): Promise<QueryResult> => {
    try {
      const currentYear = year || new Date().getFullYear();
      const startOfYear = new Date(currentYear, 0, 1).toISOString();
      const endOfYear = new Date(currentYear, 11, 31, 23, 59, 59).toISOString();
      
      const { data, error } = await supabase
        .from('contracts')
        .select('contract_type_id, contract_types(name), expected_cost, currency_id, currencies(symbol)')
        .gte('created_at', startOfYear)
        .lte('created_at', endOfYear);
      
      if (error) throw error;
      
      // Group by contract type and sum costs
      const costsByType: Record<string, { total: number, currencyId: string, symbol: string }> = {};
      
      data.forEach(contract => {
        const typeName = contract.contract_types?.name || 'Uncategorized';
        const cost = parseFloat(String(contract.expected_cost || '0'));
        const currencyId = contract.currency_id || 'unknown';
        const symbol = contract.currencies?.symbol || '$';
        
        if (!costsByType[typeName]) {
          costsByType[typeName] = { total: 0, currencyId, symbol };
        }
        
        costsByType[typeName].total += cost;
      });
      
      // Convert to array format for charts
      const costDistribution = Object.entries(costsByType).map(([name, data]) => ({
        name,
        value: data.total,
        currencyId: data.currencyId,
        symbol: data.symbol
      }));
      
      return {
        data: { costDistribution },
        error: null
      };
    } catch (error) {
      console.error("Error fetching contract costs by type:", error);
      return {
        data: { costDistribution: [] },
        error
      };
    }
  }
};
